import { useState } from 'react'
import type { Supply } from '../api/types'
import {
  Amt,
  Card,
  Dt,
  Empty,
  Field,
  LONG_LIST_CAP,
  LongListNote,
  Note,
  PageHead,
  Wt,
} from '../components/ui'
import { PersonPicker } from '../components/PersonPicker'
import { useApi } from '../hooks/useApi'
import { useVendorPicker } from '../hooks/usePickers'
import { today } from '../lib/format'

/**
 * The day book: every grower deliveries for a day, or one grower across a
 * stretch of days.
 *
 * It deliberately shows nothing until a date or a name has been chosen. Every
 * delivery ever made is not the answer to any question anybody actually has,
 * and a screen that opens with a thousand rows trains people to scroll past
 * them.
 */
export function DayBookPage() {
  const vendors = useVendorPicker()
  const [from, setFrom] = useState(today())
  const [to, setTo] = useState(today())
  const [vendorId, setVendorId] = useState<string | null>(null)
  const [asked, setAsked] = useState(false)

  const query = new URLSearchParams({ from, to })
  if (vendorId) query.set('vendorId', vendorId)

  const supplies = useApi<Supply[]>(asked ? `/supplies/daybook?${query}` : null, [
    from,
    to,
    vendorId,
  ])

  const rows = supplies.data ?? []
  const totalWeight = rows.reduce((sum, row) => sum + Number(row.weight), 0)
  const totalAmount = rows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0)
  const totalKiraya = rows.reduce((sum, row) => sum + Number(row.kiraya), 0)

  // Drawn rows stop at two hundred; the three totals above are over every row.
  // See LONG_LIST_CAP.
  const shown = rows.length > LONG_LIST_CAP ? rows.slice(0, LONG_LIST_CAP) : rows

  return (
    <>
      <PageHead title="روزنامچہ" hint="تاریخ یا زمیندار چن کر دیکھیں۔" />

      <Card>
        <div className="row">
          <Field label="اس تاریخ سے">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>

          <Field label="اس تاریخ تک">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>

          <PersonPicker
            label="زمیندار (ضروری نہیں)"
            noun="زمیندار"
            vendor
            value={vendorId}
            onChange={setVendorId}
            people={vendors.people}
            recentIds={vendors.recentIds}
            loading={vendors.loading}
            clearLabel="سب زمیندار"
          />

          <div className="field">
            <label>&nbsp;</label>
            <button type="button" className="btn" onClick={() => setAsked(true)}>
              دکھائیں
            </button>
          </div>
        </div>
      </Card>

      {supplies.error && <Note kind="error">{supplies.error}</Note>}
      {!asked && <Empty>اوپر تاریخ یا زمیندار چن کر دکھائیں دبائیں۔</Empty>}

      {asked && (
        <Card title="سپلائی">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>تاریخ</th>
                  <th>زمیندار</th>
                  <th>وزن</th>
                  <th>ریٹ</th>
                  <th>رقم</th>
                  <th className="col-extra">کرایہ</th>
                  <th className="col-extra">کمیشن</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Dt value={row.date} />
                    </td>
                    <td>{row.vendorName}</td>
                    <td className="num">
                      <Wt value={row.weight} />
                    </td>
                    <td className="num">
                      {row.awaitingRate ? (
                        <span className="pill pill--wait">ریٹ باقی</span>
                      ) : (
                        <Amt value={row.rate} />
                      )}
                    </td>
                    <td className="num">{row.amount ? <Amt value={row.amount} /> : '—'}</td>
                    <td className="num col-extra">
                      <Amt value={row.kiraya} />
                    </td>
                    <td className="num col-extra">
                      <span className="amt amt--muted">{row.commissionPct}%</span>
                    </td>
                  </tr>
                ))}

                {rows.length > 0 && (
                  <tr className="is-total">
                    <td colSpan={2}>کل</td>
                    <td className="num">
                      <Wt value={totalWeight} />
                    </td>
                    <td></td>
                    <td className="num">
                      <Amt value={totalAmount} />
                    </td>
                    <td className="num">
                      <Amt value={totalKiraya} />
                    </td>
                    <td></td>
                  </tr>
                )}

                {rows.length === 0 && !supplies.loading && (
                  <tr>
                    <td colSpan={7}>
                      <Empty>اِن دنوں میں کوئی سپلائی نہیں آئی۔</Empty>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <LongListNote shown={shown.length} total={rows.length} />
        </Card>
      )}
    </>
  )
}
